import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { BASE_URL } from '../services/api';

const formatCurrency = (num) =>
    new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(num || 0);

const pickFirstValue = (source, keys = []) => {
    for (const key of keys) {
        const value = source?.[key];
        if (value !== undefined && value !== null && String(value).trim() !== '') {
            return value;
        }
    }

    return '';
};

const getBankFileRow = (employee) => {
    const bankFields = employee?.bank_details || employee?.bankDetail || employee?.bank || {};
    const fullName = pickFirstValue(employee, ['full_name', 'name']);
    const fallbackName = [employee?.first_name, employee?.last_name]
        .filter(Boolean)
        .join(' ')
        .trim();

    const accountNumber =
        pickFirstValue(employee, ['account_number', 'bank_account_number', 'bank_account_no']) ||
        pickFirstValue(bankFields, ['account_number', 'bank_account_number', 'bank_account_no']) ||
        '';
    const ifscCode =
        pickFirstValue(employee, ['ifsc_code', 'ifsc', 'bank_ifsc_code']) ||
        pickFirstValue(bankFields, ['ifsc_code', 'ifsc', 'bank_ifsc_code']) ||
        '';
    const bankName =
        pickFirstValue(employee, ['bank_name']) ||
        pickFirstValue(bankFields, ['bank_name']) ||
        '';
    const branchName =
        pickFirstValue(employee, ['branch_name', 'bank_branch_name']) ||
        pickFirstValue(bankFields, ['branch_name', 'bank_branch_name']) ||
        '';

    return {
        employeeName: fullName || fallbackName || '-',
        employeeId: pickFirstValue(employee, ['code', 'employee_code', 'employee_id']) || '-',
        accountNumber: accountNumber || 'Missing',
        ifscCode: ifscCode || 'Missing',
        bankName: bankName || 'Missing',
        branchName: branchName || 'Missing',
        netSalary: employee?.net_salary || employee?.net_pay || 0,
        missingBankDetails: !accountNumber || !ifscCode || !bankName || !branchName
    };
};

const getCompanyField = (companyDetails, keys = [], fallback = '') => {
    for (const key of keys) {
        const value = companyDetails?.[key];
        if (value !== undefined && value !== null && String(value).trim() !== '') {
            return value;
        }
    }

    return fallback;
};

const getAbsoluteAssetUrl = (path) => {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    if (String(path).startsWith('/')) return `${BASE_URL}${path}`;
    return `${BASE_URL}/upload/User/${path}`;
};

const loadImageAsDataUrl = async (imagePath) => {
    const url = getAbsoluteAssetUrl(imagePath);
    if (!url) return null;

    try {
        const response = await fetch(url);
        if (!response.ok) return null;

        const blob = await response.blob();
        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        return null;
    }
};

export const generatePayrollPDF = (payrollData, companyDetails = null) => {
    if (!payrollData?.emp_salary?.length) {
        throw new Error('No payroll data available to export.');
    }

    const doc = new jsPDF('landscape', 'mm', 'a4');
    
    // Page dimensions
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 8;

    // Colors
    const primaryColor = [101, 87, 255];
    const headerBg = [249, 250, 251];
    const textDark = [31, 41, 55];
    const textLight = [107, 114, 128];

    // Helper function to format numbers
    const formatNumber = (num) => {
        return new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(num || 0);
    };

    // Add header function for reuse on each page
    const addHeader = () => {
        // Company Header
        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 30, 'F');

        // Company Name
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text(companyDetails?.name || 'Company Name', margin, 12);

        // Company Details
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        if (companyDetails?.address) {
            doc.text(companyDetails.address, margin, 18);
        }
        if (companyDetails?.email || companyDetails?.phone) {
            doc.text(
                `${companyDetails.email || ''} | ${companyDetails.phone || ''}`,
                margin,
                23
            );
        }

        // Report Title
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Payroll Report', pageWidth - margin, 12, { align: 'right' });
        
        // Month and Year
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.text(`${payrollData.month} ${payrollData.year}`, pageWidth - margin, 18, { align: 'right' });
    };

    // Add footer function
    const addFooter = () => {
        const footerY = pageHeight - 8;
        doc.setFontSize(7);
        doc.setTextColor(...textLight);
        doc.setFont('helvetica', 'normal');
        
        // Page number
        const pageCount = doc.internal.getNumberOfPages();
        doc.text(
            `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
            pageWidth / 2,
            footerY,
            { align: 'center' }
        );
        
        // Generated date
        const generatedDate = new Date().toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
        doc.text(`Generated: ${generatedDate}`, pageWidth - margin, footerY, { align: 'right' });
        
        // Left footer text
        doc.text('Confidential Document', margin, footerY);
    };

    // First page - add header
    addHeader();

    // Summary Section
    let yPos = 35;
    
    doc.setFillColor(...headerBg);
    doc.rect(margin, yPos, pageWidth - 2 * margin, 7, 'F');
    
    doc.setTextColor(...textDark);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Payroll Summary', margin + 2, yPos + 4.5);

    yPos += 10;

    // Summary boxes - compact
    const summaryData = [
        { label: 'Total Employees', value: payrollData.emp_salary.length },
        { label: 'Working Days', value: payrollData.working_days },
        { label: 'Weekly Holidays', value: payrollData.weekly_holidays },
        { label: 'Days in Month', value: payrollData.days_in_month }
    ];

    const boxWidth = (pageWidth - 2 * margin - 12) / 4;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    
    summaryData.forEach((item, index) => {
        const xPos = margin + (index * (boxWidth + 4));
        
        doc.setDrawColor(229, 231, 235);
        doc.setLineWidth(0.3);
        doc.roundedRect(xPos, yPos, boxWidth, 10, 1.5, 1.5, 'S');
        
        doc.setTextColor(...textLight);
        doc.text(item.label, xPos + 2, yPos + 4);
        
        doc.setTextColor(...textDark);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(String(item.value), xPos + 2, yPos + 8);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
    });

    yPos += 14;

    // Financial Summary - compact
    const financialData = [
        { label: 'Total Gross Pay', value: `Rs ${formatNumber(payrollData.total_gross_pay)}` },
        { label: 'Total Net Pay', value: `Rs ${formatNumber(payrollData.total_net_pay)}` },
        { label: 'Total Payroll Cost', value: `Rs ${formatNumber(payrollData.total_payroll_cost)}` }
    ];

    const finBoxWidth = (pageWidth - 2 * margin - 8) / 3;
    
    financialData.forEach((item, index) => {
        const xPos = margin + (index * (finBoxWidth + 4));
        
        doc.setFillColor(101, 87, 255);
        doc.roundedRect(xPos, yPos, finBoxWidth, 10, 1.5, 1.5, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.text(item.label, xPos + 2, yPos + 4);
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(item.value, xPos + 2, yPos + 8);
        doc.setFont('helvetica', 'normal');
    });

    yPos += 14;

    // Employee Table Header
    doc.setFillColor(...headerBg);
    doc.rect(margin, yPos, pageWidth - 2 * margin, 7, 'F');
    
    doc.setTextColor(...textDark);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Employee Salary Details', margin + 2, yPos + 4.5);

    yPos += 9;

    // Calculate totals
    const totals = {
        totalEPF: payrollData.emp_salary.reduce((sum, emp) => sum + (emp.total_epf || 0), 0),
        totalEPS: payrollData.emp_salary.reduce((sum, emp) => sum + (emp.ac_epf || 0), 0),
        totalESI: payrollData.emp_salary.reduce((sum, emp) => sum + (emp.total_esi || 0), 0),
        totalBasic: payrollData.emp_salary.reduce((sum, emp) => sum + (emp.basicmonthly || 0), 0),
        totalGross: payrollData.emp_salary.reduce((sum, emp) => sum + (emp.monthlyGrossByAtt || 0), 0),
        totalOTHours: payrollData.emp_salary.reduce((sum, emp) => sum + (emp.totalOtHours || 0), 0),
        totalOTPay: payrollData.emp_salary.reduce((sum, emp) => sum + (emp.otPay || 0), 0),
        totalTDS: payrollData.emp_salary.reduce((sum, emp) => sum + (emp.tenetive_tds || 0), 0),
        total87A: payrollData.emp_salary.reduce((sum, emp) => sum + (emp.under_87a_tax || 0), 0),
        totalLoan: payrollData.emp_salary.reduce((sum, emp) => sum + (emp.loanAmount || 0), 0),
        totalProfTax: payrollData.emp_salary.reduce((sum, emp) => sum + (emp.monthly_prof_tax || 0), 0),
        totalNetSalary: payrollData.emp_salary.reduce((sum, emp) => sum + (emp.net_salary || 0), 0)
    };

    // Prepare table data with optimized formatting
    const tableData = payrollData.emp_salary.map((emp, index) => [
        index + 1,
        emp.code || '',
        emp.tax_regime_used || emp.empSelectedTaxRegime || 'New',
        emp.full_name || '',
        `${emp.paid_days || 0}/${payrollData.working_days}`,
        emp.weekly_holidays || 0,
        formatNumber(emp.total_epf || 0),
        formatNumber(emp.ac_epf || 0),
        formatNumber(emp.total_esi || 0),
        formatNumber(emp.basicmonthly || 0),
        formatNumber(emp.gross_pay || 0),
        formatNumber(emp.monthlyGrossByAtt || 0),
        formatNumber(emp.totalOtHours || 0),
        formatNumber(emp.otPay || 0),
        formatNumber(emp.tenetive_tds || 0),
        formatNumber(emp.under_87a_tax || 0),
        formatNumber(emp.loanAmount || 0),
        formatNumber(emp.monthly_prof_tax || 0),
        formatNumber(emp.net_salary || 0)
    ]);

    // Add totals row
    tableData.push([
        { content: 'TOTAL', colSpan: 6, styles: { fontStyle: 'bold', halign: 'right', fillColor: [243, 244, 246] } },
        formatNumber(totals.totalEPF),
        formatNumber(totals.totalEPS),
        formatNumber(totals.totalESI),
        formatNumber(totals.totalBasic),
        formatNumber(payrollData.total_gross_pay),
        formatNumber(totals.totalGross),
        formatNumber(totals.totalOTHours),
        formatNumber(totals.totalOTPay),
        formatNumber(totals.totalTDS),
        formatNumber(totals.total87A),
        formatNumber(totals.totalLoan),
        formatNumber(totals.totalProfTax),
        formatNumber(totals.totalNetSalary)
    ]);

    // Generate table with optimized column widths
    autoTable(doc, {
        startY: yPos,
        head: [[
            '#',
            'Code',
            'Tax',
            'Employee Name',
            'Att.',
            'Hol',
            'EPF',
            'EPS',
            'ESI',
            'Basic',
            'Monthly Gross',
            'Effective Gross',
            'OT Hrs',
            'OT Pay',
            'TDS',
            '87A Rebate',
            'Loan',
            'P.Tax',
            'Net Salary'
        ]],
        body: tableData,
        theme: 'grid',
        styles: {
            fontSize: 6.5,
            cellPadding: 1.5,
            lineColor: [229, 231, 235],
            lineWidth: 0.1,
            overflow: 'linebreak',
            cellWidth: 'wrap'
        },
        headStyles: {
            fillColor: primaryColor,
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'center',
            valign: 'middle',
            fontSize: 6.5,
            cellPadding: 2
        },
        columnStyles: {
            0: { cellWidth: 7, halign: 'center' },
            1: { cellWidth: 13, halign: 'center', fontSize: 6 },
            2: { cellWidth: 10, halign: 'center', fontSize: 6 },
            3: { cellWidth: 30, halign: 'left', fontSize: 6.5 },
            4: { cellWidth: 11, halign: 'center', fontSize: 6 },
            5: { cellWidth: 9, halign: 'center', fontSize: 6 },
            6: { cellWidth: 13, halign: 'right', fontSize: 6 },
            7: { cellWidth: 13, halign: 'right', fontSize: 6 },
            8: { cellWidth: 13, halign: 'right', fontSize: 6 },
            9: { cellWidth: 15, halign: 'right', fontSize: 6 },
            10: { cellWidth: 15, halign: 'right', fontSize: 6 },
            11: { cellWidth: 16, halign: 'right', fontSize: 6 },
            12: { cellWidth: 11, halign: 'right', fontSize: 6 },
            13: { cellWidth: 13, halign: 'right', fontSize: 6 },
            14: { cellWidth: 13, halign: 'right', fontSize: 6 },
            15: { cellWidth: 12, halign: 'right', fontSize: 6 },
            16: { cellWidth: 11, halign: 'right', fontSize: 6 },
            17: { cellWidth: 13, halign: 'right', fontSize: 6 },
            18: { cellWidth: 18, halign: 'right', fontStyle: 'bold', textColor: [5, 150, 105], fontSize: 6.5 }
        },
        alternateRowStyles: {
            fillColor: [249, 250, 251]
        },
        didDrawPage: function (data) {
            // Add header on each page
            if (doc.internal.getCurrentPageInfo().pageNumber > 1) {
                addHeader();
            }
            // Add footer on each page
            addFooter();
        },
        margin: { top: 35, left: margin, right: margin, bottom: 12 }
    });

    // Get finalY from the last table
    const finalY = (doc).lastAutoTable.finalY || yPos + 100;
    
    if (finalY < pageHeight - 40) {
        doc.setFillColor(...primaryColor);
        doc.rect(margin, finalY + 5, pageWidth - 2 * margin, 0.3, 'F');
        
        doc.setTextColor(...textDark);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(
            'Note: This is a computer-generated document and does not require a signature.',
            pageWidth / 2,
            finalY + 12,
            { align: 'center' }
        );
    }

    // Save PDF
    const fileName = `Payroll_${payrollData.month}_${payrollData.year}.pdf`;
    doc.save(fileName);
    return fileName;
};

export const generateBankFilePDF = async (payrollData, companyDetails = null) => {
    if (!payrollData?.emp_salary?.length) {
        throw new Error('No payroll data available to export.');
    }

    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 6;
    const primaryColor = [101, 87, 255];
    const mutedText = [107, 114, 128];
    const darkText = [31, 41, 55];
    const rows = (payrollData?.emp_salary || []).map(getBankFileRow);
    const missingBankEmployees = rows.filter((row) => row.missingBankDetails).length;
    const totalNetSalary = rows.reduce((sum, row) => sum + (row.netSalary || 0), 0);
    const logoDataUrl = await loadImageAsDataUrl(companyDetails?.logo);
    const companyName = getCompanyField(companyDetails, ['name', 'company_name'], 'Cedur');
    const companyAddress = getCompanyField(companyDetails, ['address', 'company_address']);
    const companyPan = getCompanyField(companyDetails, ['pan', 'company_pan']);
    const companyGst = getCompanyField(companyDetails, ['gst', 'company_gst']);
    const companyPhone = getCompanyField(companyDetails, ['phone', 'phone_number']);
    const companyEmail = getCompanyField(companyDetails, ['email']);
    const headerTextX = logoDataUrl ? margin + 22 : margin;

    const addHeader = () => {
        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 30, 'F');

        if (logoDataUrl) {
            doc.setFillColor(255, 255, 255);
            doc.roundedRect(margin, 5.5, 16, 16, 3, 3, 'F');
            doc.addImage(logoDataUrl, 'JPEG', margin + 1.2, 6.7, 13.6, 13.6);
        }

        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.text(companyName, headerTextX, 11.5);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        const infoLineOne = [companyPan ? `PAN: ${companyPan}` : '', companyGst ? `GST: ${companyGst}` : '']
            .filter(Boolean)
            .join(' | ');
        const infoLineTwo = [companyPhone, companyEmail].filter(Boolean).join(' | ');

        if (infoLineOne) {
            doc.text(infoLineOne, headerTextX, 16.5);
        }
        if (infoLineTwo) {
            doc.text(infoLineTwo, headerTextX, 20.5);
        }
        if (companyAddress) {
            doc.text(doc.splitTextToSize(companyAddress, 120), headerTextX, 24.5);
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text('Bank Transfer File', pageWidth - margin, 10.5, { align: 'right' });

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(`${payrollData.month} ${payrollData.year}`, pageWidth - margin, 16.5, { align: 'right' });
        const metaParts = [
            payrollData.financial_year ? `FY ${payrollData.financial_year}` : '',
            payrollData.engine_version_label ? `Version ${payrollData.engine_version_label}` : '',
            `Employees ${rows.length}`
        ].filter(Boolean);
        doc.text(metaParts.join(' | '), pageWidth - margin, 22, { align: 'right' });
    };

    const addFooter = () => {
        const footerY = pageHeight - 8;
        const pageCount = doc.internal.getNumberOfPages();
        const generatedDate = new Date().toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...mutedText);
        doc.text(`Generated: ${generatedDate}`, margin, footerY);
        doc.text(
            `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
            pageWidth - margin,
            footerY,
            { align: 'right' }
        );
    };

    addHeader();

    doc.setTextColor(...darkText);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Salary Disbursement Details', margin, 39);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...mutedText);
    const bankStatusNote = missingBankEmployees > 0
        ? `Missing bank details for ${missingBankEmployees} employee${missingBankEmployees > 1 ? 's' : ''}. Review highlighted cells before sharing this file with the bank.`
        : 'All required bank fields are available for the employees listed below.';
    doc.text(bankStatusNote, margin, 45);

    autoTable(doc, {
        startY: 50,
        head: [[
            'Sr.',
            'Employee Name',
            'Employee ID',
            'Bank Account Number',
            'IFSC Code',
            'Bank Name',
            'Branch Name',
            'Net Salary Amount'
        ]],
        body: rows.map((row, index) => [
            index + 1,
            row.employeeName,
            row.employeeId,
            row.accountNumber,
            row.ifscCode,
            row.bankName,
            row.branchName,
            formatCurrency(row.netSalary)
        ]),
        foot: [[
            { content: 'TOTAL', colSpan: 7, styles: { halign: 'right', fontStyle: 'bold' } },
            formatCurrency(totalNetSalary)
        ]],
        theme: 'grid',
        tableWidth: pageWidth - (margin * 2),
        styles: {
            fontSize: 8,
            cellPadding: 2.2,
            lineColor: [229, 231, 235],
            lineWidth: 0.1,
            overflow: 'linebreak'
        },
        headStyles: {
            fillColor: primaryColor,
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'center',
            valign: 'middle'
        },
        footStyles: {
            fillColor: [243, 244, 246],
            textColor: darkText,
            fontStyle: 'bold'
        },
        columnStyles: {
            0: { cellWidth: 12, halign: 'center' },
            1: { cellWidth: 52 },
            2: { cellWidth: 24, halign: 'center' },
            3: { cellWidth: 58, halign: 'center' },
            4: { cellWidth: 32, halign: 'center' },
            5: { cellWidth: 44 },
            6: { cellWidth: 39 },
            7: { cellWidth: 24, halign: 'right' }
        },
        alternateRowStyles: {
            fillColor: [249, 250, 251]
        },
        margin: { top: 34, left: margin, right: margin, bottom: 14 },
        didParseCell: (data) => {
            if (data.section !== 'body') {
                return;
            }

            if (data.row.raw?.missingBankDetails && [3, 4, 5, 6].includes(data.column.index)) {
                const value = String(data.cell.raw || '').trim().toLowerCase();
                if (value === 'missing') {
                    data.cell.styles.fillColor = [254, 226, 226];
                    data.cell.styles.textColor = [185, 28, 28];
                    data.cell.styles.fontStyle = 'bold';
                }
            }
        },
        didDrawPage: () => {
            if (doc.internal.getCurrentPageInfo().pageNumber > 1) {
                addHeader();
            }
            addFooter();
        }
    });

    const fileName = `Bank_File_${payrollData.month}_${payrollData.year}.pdf`;
    doc.save(fileName);
    return fileName;
};

// Generate summary PDF (improved version)
export const generatePayrollSummaryPDF = async (payrollData, companyDetails = null) => {
    if (!payrollData?.emp_salary?.length) {
        throw new Error('No payroll data available to export.');
    }

    const doc = new jsPDF('landscape', 'mm', 'a4');

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const primaryColor = [101, 87, 255];
    const textDark = [31, 41, 55];
    const mutedText = [107, 114, 128];
    const lightBorder = [229, 231, 235];
    const zebra = [249, 250, 251];
    const logoDataUrl = await loadImageAsDataUrl(companyDetails?.logo);

    const formatNumber = (num) => {
        return new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(num || 0);
    };

    const formatAmount = (num) => `Rs. ${formatNumber(num)}`;

    const addFooter = () => {
        const footerY = pageHeight - 7;
        doc.setFontSize(8);
        doc.setTextColor(...mutedText);

        const pageCount = doc.internal.getNumberOfPages();
        doc.text(
            `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
            pageWidth / 2,
            footerY,
            { align: 'center' }
        );

        const generatedDate = new Date().toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
        doc.text(`Generated: ${generatedDate}`, pageWidth - margin, footerY, { align: 'right' });
    };

    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 28, 'F');

    if (logoDataUrl) {
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(margin, 5, 18, 18, 3, 3, 'F');
        doc.addImage(logoDataUrl, 'JPEG', margin + 1.5, 6.5, 15, 15);
    }

    const companyName = getCompanyField(companyDetails, ['name', 'company_name'], 'Cedur');
    const companyAddress = getCompanyField(companyDetails, ['address', 'company_address']);
    const companyPan = getCompanyField(companyDetails, ['pan', 'company_pan']);
    const companyGst = getCompanyField(companyDetails, ['gst', 'company_gst']);
    const companyPhone = getCompanyField(companyDetails, ['phone', 'phone_number']);
    const companyEmail = getCompanyField(companyDetails, ['email']);

    const headerTextX = logoDataUrl ? margin + 22 : margin;

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(companyName, headerTextX, 11);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const infoLineOne = [companyPan ? `PAN: ${companyPan}` : '', companyGst ? `GST: ${companyGst}` : '']
        .filter(Boolean)
        .join(' | ');
    const infoLineTwo = [companyPhone, companyEmail].filter(Boolean).join(' | ');

    if (infoLineOne) {
        doc.text(infoLineOne, headerTextX, 16);
    }
    if (infoLineTwo) {
        doc.text(infoLineTwo, headerTextX, 20);
    }
    if (companyAddress) {
        doc.text(doc.splitTextToSize(companyAddress, 120), headerTextX, 24);
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('Payroll Report', pageWidth - margin, 10, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`${payrollData.month} ${payrollData.year}`, pageWidth - margin, 16, { align: 'right' });

    const headerMeta = [
        payrollData.financial_year ? `FY ${payrollData.financial_year}` : '',
        payrollData.engine_version_label ? `Version ${payrollData.engine_version_label}` : '',
        `Employees ${payrollData.emp_salary.length}`,
    ].filter(Boolean).join(' | ');
    doc.setFontSize(8);
    doc.text(headerMeta, pageWidth - margin, 21, { align: 'right' });

    let yPos = 34;

    const summaryRows = [
        ['Employees', payrollData.emp_salary.length],
        ['Working Days', payrollData.working_days],
        ['Weekly Holidays', payrollData.weekly_holidays],
        ['Days in Month', payrollData.days_in_month],
        ['Gross Pay', formatAmount(payrollData.total_gross_pay || 0)],
        ['Net Pay', formatAmount(payrollData.total_net_pay || 0)],
        ['Payroll Cost', formatAmount(payrollData.total_payroll_cost || 0)],
        ['Average Salary', formatAmount(payrollData.emp_salary.length > 0 ? (payrollData.total_net_pay || 0) / payrollData.emp_salary.length : 0)],
    ];

    autoTable(doc, {
        startY: yPos,
        body: summaryRows,
        theme: 'grid',
        styles: {
            fontSize: 8.5,
            cellPadding: { top: 2.8, right: 4, bottom: 2.8, left: 4 },
            lineColor: lightBorder,
            lineWidth: 0.1
        },
        columnStyles: {
            0: { cellWidth: 38, fontStyle: 'bold', textColor: mutedText },
            1: { cellWidth: 38, halign: 'right', fontStyle: 'bold', textColor: textDark }
        },
        tableWidth: 76,
        margin: { left: margin }
    });

    const totalNetPay = payrollData.total_net_pay ||
        payrollData.emp_salary.reduce((sum, emp) => sum + (emp.net_salary || 0), 0);

    const financialData = [
        ['Total Gross Pay', formatAmount(payrollData.total_gross_pay || 0)],
        ['Total Net Pay', formatAmount(totalNetPay)],
        ['Total Payroll Cost', formatAmount(payrollData.total_payroll_cost || 0)],
        ['Average Salary', formatAmount(payrollData.emp_salary.length > 0 ? totalNetPay / payrollData.emp_salary.length : 0)]
    ];

    const financialStartX = margin + 82;
    autoTable(doc, {
        startY: yPos,
        body: financialData,
        theme: 'grid',
        styles: {
            fontSize: 9,
            cellPadding: { top: 2.8, right: 4, bottom: 2.8, left: 4 },
            lineColor: lightBorder,
            lineWidth: 0.1,
            valign: 'middle'
        },
        columnStyles: {
            0: { cellWidth: 48, fontStyle: 'bold', textColor: mutedText },
            1: { cellWidth: 44, halign: 'right', fontStyle: 'bold', textColor: textDark, fontSize: 9.5 }
        },
        alternateRowStyles: {
            fillColor: zebra
        },
        tableWidth: 92,
        margin: { left: financialStartX }
    });

    yPos = Math.max(
        doc.lastAutoTable.finalY || yPos,
        (doc.lastAutoTable?.finalY || yPos)
    ) + 8;

    const employeeData = payrollData.emp_salary.map((emp, index) => [
        index + 1,
        emp.code || '',
        emp.full_name || '',
        emp.tax_regime_used || '',
        `${emp.paid_days || emp.present || 0}/${payrollData.working_days}`,
        formatAmount(emp.gross_pay || emp.monthlyGrossByAtt || 0),
        formatAmount(emp.tenetive_tds || 0),
        formatAmount(emp.net_salary || 0)
    ]);

    autoTable(doc, {
        startY: yPos,
        head: [['#', 'Code', 'Name', 'Regime', 'Attendance', 'Gross', 'TDS', 'Net Salary']],
        body: employeeData,
        theme: 'grid',
        styles: {
            fontSize: 8,
            cellPadding: 2.5,
            lineColor: lightBorder,
            lineWidth: 0.1
        },
        headStyles: {
            fillColor: primaryColor,
            textColor: [255, 255, 255],
            fontStyle: 'bold'
        },
        columnStyles: {
            0: { cellWidth: 10, halign: 'center' },
            1: { cellWidth: 24, halign: 'center' },
            2: { cellWidth: 64 },
            3: { cellWidth: 20, halign: 'center' },
            4: { cellWidth: 24, halign: 'center' },
            5: { cellWidth: 28, halign: 'right' },
            6: { cellWidth: 24, halign: 'right' },
            7: { cellWidth: 'auto', halign: 'right', fontStyle: 'bold' }
        },
        alternateRowStyles: {
            fillColor: zebra
        },
        didDrawPage: () => {
            addFooter();
        },
        margin: { left: margin, right: margin, bottom: 12 }
    });

    const fileName = `Payroll_Summary_${payrollData.month}_${payrollData.year}.pdf`;
    doc.save(fileName);
    return fileName;
};
